import React, { Component } from 'react';
import PropTypes from 'prop-types';
import commaNumber from 'comma-number';
import _ from 'lodash';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-regular-svg-icons';

import './LinkResults.scss';

import LinkResult from './LinkResult';
import LinksPagination from './LinksPagination';
import EmptyLinkResults from './EmptyLinkResults';
import RequiresPermissions from '../Managers/RequiresPermissions';
import Loader from '../Loader/Loader';
import Tooltip from '../General/Tooltip';

import { getPrettyNumber } from '../../Helpers/formatting';
import { isBrand } from '../../Helpers/user_helpers';
import { isAdminControlMode } from '../../Helpers/ui_helpers';
import { blockOnRequiringSubscription, isSubscribedToFeature } from '../../Helpers/subscription_helpers';

class LinkResults extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
    meta: PropTypes.object.isRequired,
    ui: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    results: PropTypes.array.isRequired,
    fetching: PropTypes.bool.isRequired,
    transitioningTabs: PropTypes.bool.isRequired,
    setSort: PropTypes.func.isRequired,
    sortDirection: PropTypes.string.isRequired,
    searchVal: PropTypes.string.isRequired,
    sortOrder: PropTypes.string.isRequired,
    groupByMode: PropTypes.string.isRequired,
    selectedList: PropTypes.object,
    openArtistModal: PropTypes.func.isRequired,
    editPin: PropTypes.func.isRequired,
    togglePinToMove: PropTypes.func.isRequired,
    toggleAddingMode: PropTypes.func.isRequired,
    updateSearchVal: PropTypes.func.isRequired,
    selectList: PropTypes.func.isRequired,
    deletePin: PropTypes.func.isRequired,
    goToPage: PropTypes.func.isRequired,
    isSuperAdminMode: PropTypes.bool.isRequired
  };

  clickFilter = filter => {
    this.props.setSort(filter, this.props.sortOrder !== filter || this.props.sortDirection === 'asc' ? 'desc' : 'asc');
  };

  getHeaderCell = (name, additionalClasses, sortVar, options = {}) => {
    const { fetching, sortOrder, sortDirection, user, ui } = this.props;
    const { requiresFeature, requiresPermission, metric, isMetricDollarValue, showMetricAsDollars } = options;
    const classes = cn('cell', additionalClasses, { clickable: sortVar });
    const isBlocked = isBrand(user) && !!requiresFeature && !isSubscribedToFeature(user, requiresFeature) && !isAdminControlMode(ui);
    const click = () =>
      isBrand(user)
        ? sortVar && (!isBlocked || !blockOnRequiringSubscription(user, requiresFeature)) && this.clickFilter(sortVar)
        : this.clickFilter(sortVar);
    const isCurSort = sortVar && sortOrder === sortVar;
    const tooltipDisplay = isBlocked || !metric ? '' : `${isMetricDollarValue ? '$' : ''}${commaNumber(metric.toFixed(0))}`;
    return requiresPermission ? (
      <RequiresPermissions permission={requiresPermission} hideMessage>
        <div onClick={click} className={classes}>
          {sortVar && (
            <FontAwesomeIcon
              className={cn('sort', { active: isCurSort })}
              icon={isCurSort ? (sortDirection === 'desc' ? faArrowDown : faArrowUp) : faArrowDown}
            />
          )}
          {name}
          {!!metric && (
            <Tooltip interactive message={tooltipDisplay}>
              <div className={cn('metric', { fetching })}>
                {showMetricAsDollars ? '$' : ''}
                {getPrettyNumber(Math.round(metric))}
                {fetching && <Loader size={24} />}
              </div>
            </Tooltip>
          )}
        </div>
      </RequiresPermissions>
    ) : (
      <div onClick={click} className={classes}>
        {sortVar && (
          <FontAwesomeIcon
            className={cn('sort', { active: isCurSort })}
            icon={isCurSort ? (sortDirection === 'desc' ? faArrowDown : faArrowUp) : faArrowDown}
          />
        )}
        {name}
        {!!metric && (
          <Tooltip interactive message={tooltipDisplay}>
            <div className={cn('metric', { fetching })}>
              {isBlocked ? (
                <>
                  VIEW
                  {fetching && <Loader size={24} />}
                </>
              ) : (
                <>
                  {showMetricAsDollars ? '$' : ''}
                  {getPrettyNumber(Math.round(metric))}
                  {fetching && <Loader size={24} />}
                </>
              )}
            </div>
          </Tooltip>
        )}
      </div>
    );
  };

  render() {
    const {
      user,
      ui,
      meta,
      store,
      results,
      searchVal,
      groupByMode,
      toggleAddingMode,
      updateSearchVal,
      fetching,
      transitioningTabs,
      selectedList,
      isSuperAdminMode
    } = this.props;
    const { summary } = meta || {};
    const { commissionTotal, mentions, orderVolumeTotal, estimatedMediaValue, ordersTotal, views } = summary || {};
    return (
      <div className='link-results-outer-container'>
        <div className='row header'>
          {groupByMode === 'users' ? (
            isSuperAdminMode ? (
              <>
                {this.getHeaderCell('User', 'xl')}
                {this.getHeaderCell('Links Created', 'sm', 'mentions', { requiresFeature: 'ANALYTICS', metric: mentions })}
                {this.getHeaderCell('Clicks', 'sm', 'views', { requiresFeature: 'ANALYTICS', metric: views })}
                {this.getHeaderCell('Orders', 'sm', 'ordersTotal', { requiresFeature: 'ANALYTICS', metric: ordersTotal })}
                {this.getHeaderCell('Volume', 'sm', 'orderVolumeTotal', {
                  requiresFeature: 'ANALYTICS',
                  metric: orderVolumeTotal,
                  isMetricDollarValue: true
                })}
                {this.getHeaderCell('Commission', 'sm', 'commissionTotal', {
                  requiresFeature: 'ANALYTICS',
                  metric: commissionTotal,
                  isMetricDollarValue: true
                })}
                {this.getHeaderCell('EMV', 'sm', 'estimatedMediaValue', {
                  requiresFeature: 'SOCIAL_MENTIONS_FEED',
                  metric: estimatedMediaValue,
                  isMetricDollarValue: true
                })}
                {this.getHeaderCell('First Linked', 'md', 'createdAt')}
              </>
            ) : (
              <>
                {this.getHeaderCell('User', 'lg')}
                {this.getHeaderCell('Links Created', 'sm', 'mentions', { requiresFeature: 'ANALYTICS', metric: mentions })}
                {this.getHeaderCell('Clicks', 'sm', 'views', { requiresFeature: 'ANALYTICS', metric: views })}
                {this.getHeaderCell('Orders', 'sm', 'ordersTotal', { requiresFeature: 'ANALYTICS', metric: ordersTotal })}
                {this.getHeaderCell('Volume', 'sm', 'orderVolumeTotal', {
                  requiresFeature: 'ANALYTICS',
                  metric: orderVolumeTotal,
                  isMetricDollarValue: true
                })}
                {this.getHeaderCell('Commission', 'sm', 'commissionTotal', {
                  requiresFeature: 'ANALYTICS',
                  metric: commissionTotal,
                  isMetricDollarValue: true
                })}
                {this.getHeaderCell('EMV', 'sm', 'estimatedMediaValue', {
                  requiresFeature: 'SOCIAL_MENTIONS_FEED',
                  metric: estimatedMediaValue,
                  isMetricDollarValue: true
                })}
                {this.getHeaderCell('First Linked', 'md', 'createdAt')}
              </>
            )
          ) : groupByMode === 'products' ? (
            <>
              {this.getHeaderCell('Product', 'xl')}
              {isSuperAdminMode && this.getHeaderCell('Brand', 'sm')}
              {this.getHeaderCell('Mentions', 'sm', 'mentions', { requiresFeature: 'ANALYTICS', metric: mentions })}
              {this.getHeaderCell('Clicks', 'sm', 'views', { requiresFeature: 'ANALYTICS', metric: views })}
              {this.getHeaderCell('Orders', 'sm', 'ordersTotal', { requiresFeature: 'ANALYTICS', metric: ordersTotal, isMetricDollarValue: true })}
              {this.getHeaderCell('Volume', 'sm', 'orderVolumeTotal', {
                requiresFeature: 'ANALYTICS',
                metric: orderVolumeTotal,
                isMetricDollarValue: true
              })}
            </>
          ) : groupByMode === 'orders' ? (
            <>
              {this.getHeaderCell('Date', 'sm', 'createdAt')}
              {this.getHeaderCell('User', 'sm')}
              {this.getHeaderCell('Content', 'md')}
              {this.getHeaderCell('Order #', 'sm')}
              {this.getHeaderCell('Domain', 'sm')}
              {this.getHeaderCell('Network', 'sm')}
              {this.getHeaderCell('Amount', 'sm', 'orderVolumeTotal', {
                requiresFeature: 'ANALYTICS',
                metric: orderVolumeTotal,
                isMetricDollarValue: true
              })}
              {this.getHeaderCell('Commission', 'sm', 'commissionTotal', {
                requiresFeature: 'ANALYTICS',
                metric: commissionTotal,
                isMetricDollarValue: true
              })}
              {this.getHeaderCell('Code Used', 'sm')}
            </>
          ) : isSuperAdminMode ? (
            <>
              {this.getHeaderCell('Created', 'sm', 'createdAt')}
              {this.getHeaderCell('Product', 'xl')}
              {this.getHeaderCell('User', 'md')}
              {this.getHeaderCell('Content', 'md')}
              {this.getHeaderCell('Geolinks', 'sm')}
              {this.getHeaderCell('Clicks', 'sm', 'views', { metric: views })}
              {this.getHeaderCell('Orders', 'sm', 'ordersTotal', { metric: ordersTotal })}
              {this.getHeaderCell('Volume', 'sm', 'orderVolumeTotal', { metric: orderVolumeTotal, isMetricDollarValue: true })}
              {this.getHeaderCell('Commission', 'sm', 'commissionTotal', { metric: commissionTotal, isMetricDollarValue: true })}
              {this.getHeaderCell('Affiliate Link', 'lg')}
            </>
          ) : isBrand(user) ? (
            <>
              {this.getHeaderCell('Created', 'sm', 'createdAt')}
              {this.getHeaderCell('Product', 'xl')}
              {this.getHeaderCell('User', 'md')}
              {this.getHeaderCell('Content', 'md')}
              {this.getHeaderCell('Clicks', 'sm', 'views', { requiresFeature: 'ANALYTICS', metric: views })}
              {this.getHeaderCell('Orders', 'sm', 'ordersTotal', { requiresFeature: 'ANALYTICS', metric: ordersTotal })}
              {this.getHeaderCell('Volume', 'sm', 'orderVolumeTotal', {
                requiresFeature: 'ANALYTICS',
                metric: orderVolumeTotal,
                isMetricDollarValue: true
              })}
              {this.getHeaderCell('Commission', 'sm', 'commissionTotal', {
                requiresFeature: 'ANALYTICS',
                metric: commissionTotal,
                isMetricDollarValue: true
              })}
            </>
          ) : (
            <>
              {this.getHeaderCell('', 'xs')}
              {this.getHeaderCell('Created', 'sm', 'createdAt')}
              {this.getHeaderCell('Product', 'xl')}
              {this.getHeaderCell('Content', 'md')}
              {this.getHeaderCell('Clicks', 'sm', 'views', { metric: views })}
              {this.getHeaderCell('Orders', 'sm', 'ordersTotal', { requiresPermission: 'canViewEarnings', metric: ordersTotal })}
              {this.getHeaderCell('Earned', 'sm', 'commissionTotal', {
                requiresPermission: 'canViewEarnings',
                metric: commissionTotal,
                showMetricAsDollars: true,
                isMetricDollarValue: true
              })}
              {this.getHeaderCell('Affiliate Link', 'lg')}
            </>
          )}
        </div>
        {!!results.length ? (
          <div className={cn('rows body', { fetching, transitioning: transitioningTabs })}>
            {results.map(result => (
              <LinkResult
                key={result.id || result.User_id || result.Product_id}
                store={store}
                user={user}
                ui={ui}
                result={result}
                groupByMode={groupByMode}
                deletePin={this.props.deletePin}
                editPin={this.props.editPin}
                openArtistModal={this.props.openArtistModal}
                togglePinToMove={this.props.togglePinToMove}
                isSuperAdminMode={isSuperAdminMode}
              />
            ))}
          </div>
        ) : fetching ? (
          <div className='loading-msg'>Loading...</div>
        ) : (
          <EmptyLinkResults
            updateSearchVal={updateSearchVal}
            toggleAddingMode={toggleAddingMode}
            meta={meta}
            searchVal={searchVal}
            selectedList={selectedList}
            groupByMode={this.props.groupByMode}
            selectList={this.props.selectList}
            user={user}
          />
        )}
        {!_.isEmpty(meta) && meta.total_pages > 1 && (
          <div className='pagination-container'>
            <LinksPagination goToPage={this.props.goToPage} meta={meta} />
          </div>
        )}
      </div>
    );
  }
}

export default LinkResults;
